import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Menu, Lock, Unlock, Trash2, Search, RotateCcw } from 'lucide-react';
import { Card } from '../App';
import { useResolvedCardImage } from '../lib/scryfall';
import { ManaSymbol } from './ManaSymbol';
import { BrewTooltip } from './BrewTooltip';
import {
  cardsSeenByTurn,
  probabilityLandsByTurn,
  probabilityAtLeastOne,
  combinedPlayabilitySeparateTurns,
  getProbabilityColorClass,
} from '../lib/probability';

export interface GroupedDropCardItem {
  card: Card;
  groupCount: number;
  copyIndices: number[];
  displayMV: number;
  baseMV: number;
  displayCost: string;
  isBackFace?: boolean;
  isMarkedAsLand?: boolean;
}

interface DropColumnCardTileProps {
  item: GroupedDropCardItem;
  mv: number;
  activePlan: string;
  probTargetTurn: number;
  onThePlay: boolean;
  playability: { combined: number; cardProb: number; landProb: number };
  showSideboardBtn: boolean;
  isLocked?: boolean;
  onCardClick?: (card: Card) => void;
  onHoverStart: (card: Card, e: React.MouseEvent) => void;
  onHoverMove: (e: React.MouseEvent) => void;
  onHoverEnd: () => void;
  onDecrement: (e: React.MouseEvent) => void;
  onIncrement: (e: React.MouseEvent) => void;
  onMoveToSideboard: (e: React.MouseEvent) => void;
  onShiftMVLeft?: (e: React.MouseEvent) => void;
  onShiftMVRight?: (e: React.MouseEvent) => void;
}

function getColorAccentClass(card: Card): string {
  const colors = card.colors && card.colors.length > 0 ? card.colors : card.colorIdentity || [];
  if (colors.length > 1) {
    return 'bg-gradient-to-b from-amber-300 via-yellow-500 to-amber-600';
  }
  switch ((colors[0] || '').toUpperCase()) {
    case 'W':
      return 'bg-amber-100';
    case 'U':
      return 'bg-sky-400';
    case 'B':
      return 'bg-purple-500';
    case 'R':
      return 'bg-rose-500';
    case 'G':
      return 'bg-emerald-400';
    default:
      return 'bg-slate-400';
  }
}

function getShortTypeLabel(typeLine?: string): string {
  if (!typeLine) return 'Spell';
  const front = typeLine.includes('//') ? typeLine.split('//')[0].trim() : typeLine.trim();
  const mainPart = front.split('—')[0].trim();
  return mainPart
    .replace(/\bLegendary\b/gi, 'Leg.')
    .replace(/\bEnchantment\b/gi, 'Enchant.')
    .replace(/\bPlaneswalker\b/gi, 'Walker')
    .trim();
}

export function DropColumnCardTile({
  item,
  mv,
  activePlan,
  probTargetTurn,
  onThePlay,
  playability,
  showSideboardBtn,
  isLocked = false,
  onCardClick,
  onHoverStart,
  onHoverMove,
  onHoverEnd,
  onDecrement,
  onIncrement,
  onMoveToSideboard,
  onShiftMVLeft,
  onShiftMVRight,
}: DropColumnCardTileProps) {
  const { card, groupCount, displayMV, baseMV, displayCost, isMarkedAsLand } = item;
  const hasOverride = displayMV !== baseMV;

  const displayName =
    card.isFlipped && card.backName
      ? card.backName
      : card.name.includes(' // ')
      ? card.name.split(' // ')[0]
      : card.name;

  const initialImg = card.isFlipped && card.backImageUrl ? card.backImageUrl : card.imageUrl;
  const resolvedArtUrl = useResolvedCardImage(displayName, initialImg);

  const manaPips = (displayCost.includes(' // ') ? displayCost.split(' // ')[0] : displayCost)
    .split(/(?=[{])/g)
    .filter(Boolean)
    .map((pip) => pip.replace(/[{}]/g, '').replace('/', ''));

  const combinedPct = Math.round(playability.combined * 100);
  const drawPct = Math.round(playability.cardProb * 100);
  const landPct = Math.round(playability.landProb * 100);
  const accentClass = getColorAccentClass(card);
  const shortType = getShortTypeLabel(card.type);

  return (
    <div
      className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-purple-400/50 bg-slate-950/90 shadow-sm hover:shadow-md transition-all duration-150 cursor-pointer select-none"
      onClick={() => onCardClick?.(card)}
      onMouseEnter={(e) => onHoverStart(card, e)}
      onMouseMove={onHoverMove}
      onMouseLeave={onHoverEnd}
    >
      {/* Faded Scryfall Card-Art Banner Backdrop */}
      {resolvedArtUrl && (
        <img
          src={resolvedArtUrl}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-[center_21%] opacity-[0.30] group-hover:opacity-[0.45] group-hover:scale-105 transition-all duration-300 pointer-events-none"
        />
      )}

      {/* Dark Readability Gradient Scrim */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(90deg, rgba(2,6,23,0.93) 0%, rgba(15,23,42,0.72) 55%, rgba(2,6,23,0.92) 100%)',
        }}
      />

      {/* Left Color-Identity Accent Bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${accentClass}`} />

      {/* Foreground Content */}
      <div className="relative z-10 pl-2.5 pr-1.5 py-1.5 flex flex-col gap-0.5">
        {/* Top Row: Quantity Badge + Card Name + Mana Pips */}
        <div className="flex items-center justify-between gap-1 min-h-[20px]">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span
              className={`inline-flex items-center justify-center min-w-[20px] h-[17px] px-1 rounded text-[10px] font-bold leading-none flex-shrink-0 border ${
                groupCount > 1
                  ? 'bg-amber-500/25 text-amber-300 border-amber-500/40 shadow-sm'
                  : 'bg-white/10 text-white/75 border-white/15'
              }`}
            >
              {groupCount}×
            </span>

            <span
              className={`truncate text-[11px] font-semibold tracking-tight ${
                isMarkedAsLand || card.isFlipped
                  ? 'text-purple-300'
                  : hasOverride
                  ? 'text-cyan-200'
                  : 'text-white'
              }`}
            >
              {(isMarkedAsLand || card.isFlipped) && <span className="text-purple-400 mr-0.5">⚡</span>}
              {displayName}
            </span>

            {hasOverride && (
              <BrewTooltip
                title="Custom Mana Value Override"
                badge={`${baseMV} → ${displayMV}`}
                description={`Printed Mana Value is ${baseMV}; currently evaluated in the ${displayMV}-Drop slot.`}
                accent="cyan"
              >
                <span className="px-1 py-0 text-[8px] rounded bg-purple-500/35 text-purple-200 border border-purple-400/30 font-semibold flex-shrink-0 cursor-help">
                  {displayMV > baseMV ? `${baseMV}→` : `←${baseMV}`}
                </span>
              </BrewTooltip>
            )}
          </div>

          {/* Right-aligned Mana Symbols */}
          {manaPips.length > 0 && (
            <div className="flex items-center gap-0.5 flex-shrink-0 pl-0.5">
              {manaPips.map((symbol, idx) => (
                <ManaSymbol key={idx} symbol={symbol} size="sm" />
              ))}
            </div>
          )}
        </div>

        {/* Bottom Sub-Row: Swaps cleanly from Type + Playability % to Quick Action Controls on Hover */}
        <div className="relative h-[18px] flex items-center">
          {/* Default Rest State: Short Type + On-Curve Playability Pill */}
          <div className="w-full flex items-center justify-between text-[9px] group-hover:opacity-0 group-focus-within:opacity-0 transition-opacity duration-100">
            <span className="text-white/45 truncate pr-1">{shortType}</span>
            <span className="font-mono font-semibold whitespace-nowrap flex items-center gap-1">
              <span className="text-white/35 text-[8px]">Play</span>
              <span className={getProbabilityColorClass(playability.combined)}>{combinedPct}%</span>
            </span>
          </div>

          {/* Hover / Focus Action Bar: − | ‹ | SB | › | + | Odds Info (or Locked Notice when Column Locked) */}
          {isLocked ? (
            <div className="absolute inset-0 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-100">
              <span className="inline-flex items-center gap-1 px-1.5 h-full rounded bg-amber-500/20 border border-amber-400/35 text-[8px] font-semibold text-amber-200">
                <Lock className="size-2.5 text-amber-400" />
                <span>Column Locked</span>
              </span>
              <span className="px-1 h-full inline-flex items-center rounded bg-black/60 border border-white/15 text-[8px] font-mono font-bold">
                <span className={getProbabilityColorClass(playability.combined)}>{combinedPct}%</span>
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-100">
            <BrewTooltip
              title="Remove 1 Copy (−)"
              badge={`${card.quantity} total`}
              description={`Removes 1 copy of ${card.name} from your deck.`}
              accent="red"
              className="flex-1 h-full"
            >
              <button
                type="button"
                onClick={onDecrement}
                className="w-full h-full flex items-center justify-center rounded bg-red-500/25 hover:bg-red-500/45 text-red-200 border border-red-500/35 text-[11px] font-bold leading-none transition-colors"
              >
                −
              </button>
            </BrewTooltip>

            {activePlan !== 'base' && onShiftMVLeft && (
              <BrewTooltip
                title={mv > 0 ? `Shift 1 Copy to MV ${mv - 1} (‹)` : 'Minimum MV'}
                badge={`Match ${activePlan}`}
                description={
                  mv > 0
                    ? `Moves 1 copy of ${card.name} to the ${mv - 1}-Drop column for this Matchup Plan.`
                    : 'Already at MV 0.'
                }
                accent="purple"
                className="flex-1 h-full"
              >
                <button
                  type="button"
                  disabled={mv <= 0}
                  onClick={onShiftMVLeft}
                  className={`w-full h-full flex items-center justify-center rounded border text-[11px] font-bold leading-none transition-colors ${
                    mv > 0
                      ? 'bg-purple-500/25 hover:bg-purple-500/45 text-purple-200 border-purple-500/35'
                      : 'bg-white/5 text-white/20 border-white/5 cursor-not-allowed'
                  }`}
                >
                  ‹
                </button>
              </BrewTooltip>
            )}

            {showSideboardBtn && (
              <BrewTooltip
                title={activePlan === 'base' ? 'Move 1 Copy to Sideboard' : 'Side Out 1 Copy'}
                badge={activePlan === 'base' ? 'Base Deck' : `Match ${activePlan}`}
                description={
                  activePlan === 'base'
                    ? `Moves 1 copy of ${card.name} to your Sideboard.`
                    : `Sides out 1 copy of ${card.name} in this Matchup Plan.`
                }
                accent="amber"
                className="flex-1 h-full"
              >
                <button
                  type="button"
                  onClick={onMoveToSideboard}
                  className="w-full h-full flex items-center justify-center rounded bg-amber-500/25 hover:bg-amber-500/45 text-amber-200 border border-amber-500/35 text-[8px] font-bold leading-none transition-colors"
                >
                  SB
                </button>
              </BrewTooltip>
            )}

            {activePlan !== 'base' && onShiftMVRight && (
              <BrewTooltip
                title={`Shift 1 Copy to MV ${mv + 1} (›)`}
                badge={`Match ${activePlan}`}
                description={`Moves 1 copy of ${card.name} to the ${mv + 1}-Drop column for this Matchup Plan.`}
                accent="purple"
                className="flex-1 h-full"
              >
                <button
                  type="button"
                  onClick={onShiftMVRight}
                  className="w-full h-full flex items-center justify-center rounded bg-purple-500/25 hover:bg-purple-500/45 text-purple-200 border-purple-500/35 text-[11px] font-bold leading-none transition-colors"
                >
                  ›
                </button>
              </BrewTooltip>
            )}

            <BrewTooltip
              title="Add 1 Copy (+)"
              badge={`${card.quantity} → ${card.quantity + 1}`}
              description={`Adds 1 more copy of ${card.name} to your Main Deck.`}
              accent="green"
              className="flex-1 h-full"
            >
              <button
                type="button"
                onClick={onIncrement}
                className="w-full h-full flex items-center justify-center rounded bg-emerald-500/25 hover:bg-emerald-500/45 text-emerald-200 border border-emerald-500/35 text-[11px] font-bold leading-none transition-colors"
              >
                +
              </button>
            </BrewTooltip>

            <BrewTooltip
              title={`${card.name} — On-Curve Odds`}
              badge={`${combinedPct}% Playable`}
              description={`Draw Card (by T${probTargetTurn}): ${drawPct}% • Hit ${displayMV} Mana (by T${Math.max(
                probTargetTurn,
                displayMV
              )}): ${landPct}%`}
              detail={`${onThePlay ? 'On the Play' : 'On the Draw'} • Click card to edit tags/MV`}
              accent="cyan"
            >
              <span className="px-1 h-full inline-flex items-center rounded bg-black/60 border border-white/15 text-[8px] font-mono font-bold cursor-help">
                <span className={getProbabilityColorClass(playability.combined)}>{combinedPct}%</span>
              </span>
            </BrewTooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FULL #DROP COLUMN COMPONENT FOR DYNAMIC MVD CURVE VIEW
// ============================================================================

export interface RawDropCardCopy {
  card: Card;
  copyIndex: number;
  copyMV: number;
  isBackFace?: boolean;
  isMarkedAsLand?: boolean;
}

interface DropCurveColumnProps {
  mv: number;
  cardCopies: RawDropCardCopy[];
  anchorCount: number;
  isValidParity: boolean;
  probTargetTurn: number;
  rampBoost: number;
  drawBoost: number;
  effectiveTargetLands: number;
  targetDeckSize: number;
  targetManaCurve: number;
  onThePlay: boolean;
  maxMVCap: number | null;
  setMaxMVCap: (val: number | null) => void;
  minMVFloor: number | null;
  setMinMVFloor: (val: number | null) => void;
  displayDeck: Card[];
  isEffectivelyLand: (c: Card) => boolean;
  anchorDistribution: number[];
  deck: Card[];
  sideboard: Card[];
  activePlan: string;
  onCardClick?: (card: Card) => void;
  setHoveredCard: (card: Card | null) => void;
  setMousePos: (pos: { x: number; y: number }) => void;
  onRemoveCard?: (id: string) => void;
  onUpdateCard?: (id: string, updates: Partial<Card>) => void;
  onUpdatePlanCopyMVs?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', mvs: number[]) => void;
  onUpdateSideboardCard?: (id: string, updates: Partial<Card>) => void;
  onAddToSideboard?: (card: Card) => void;
  onUpdatePlanQuantity?: (cardId: string, plan: 'A' | 'B' | 'C' | 'D' | 'E', qty: number) => void;
  onAddCard?: (card: Card) => void;
  setBuildSearchMV: (mv: number | null) => void;
  isLocked?: boolean;
  onToggleLock?: () => void;
  isSingletonFormat?: boolean;
}

export function DropCurveColumn({
  mv,
  cardCopies,
  anchorCount,
  isValidParity,
  probTargetTurn,
  rampBoost,
  drawBoost,
  effectiveTargetLands,
  targetDeckSize,
  targetManaCurve,
  onThePlay,
  maxMVCap,
  setMaxMVCap,
  minMVFloor,
  setMinMVFloor,
  displayDeck,
  isEffectivelyLand,
  anchorDistribution,
  deck,
  sideboard,
  activePlan,
  onCardClick,
  setHoveredCard,
  setMousePos,
  onRemoveCard,
  onUpdateCard,
  onUpdatePlanCopyMVs,
  onUpdateSideboardCard,
  onAddToSideboard,
  onUpdatePlanQuantity,
  onAddCard,
  setBuildSearchMV,
  isLocked = false,
  onToggleLock,
  isSingletonFormat = false,
}: DropCurveColumnProps) {
  const diff = cardCopies.length - anchorCount;
  const evalTurn = mv === 0 ? probTargetTurn : mv;
  const adjustedLands = effectiveTargetLands + rampBoost;
  const seen = cardsSeenByTurn(evalTurn, onThePlay) + drawBoost;

  const castPct =
    mv === 0
      ? 100
      : mv >= 1 && targetDeckSize > 0 && adjustedLands > 0
      ? Math.round(probabilityLandsByTurn(targetDeckSize, adjustedLands, mv, seen) * 100)
      : 0;
  const castColor =
    castPct >= 70
      ? 'text-emerald-300'
      : castPct >= 50
      ? 'text-amber-300'
      : castPct >= 30
      ? 'text-orange-300'
      : 'text-rose-300';

  const copiesAtMV = cardCopies.length;
  const drawPct =
    copiesAtMV > 0 && targetDeckSize > 0 && seen > 0
      ? Math.round(probabilityAtLeastOne(targetDeckSize, copiesAtMV, seen) * 100)
      : 0;

  const playPct = Math.round((castPct * drawPct) / 100);
  const playColor =
    playPct >= 50
      ? 'text-emerald-400'
      : playPct >= 30
      ? 'text-amber-400'
      : playPct >= 15
      ? 'text-orange-400'
      : 'text-rose-400';

  // Column accent styling based on lock & slot fill status
  const columnBorderClass = isLocked
    ? 'border-amber-400/45 ring-1 ring-amber-400/15'
    : !isValidParity
    ? 'border-red-500/30'
    : diff === 0 && anchorCount > 0
    ? 'border-emerald-500/35'
    : diff > 0
    ? 'border-amber-500/35'
    : 'border-white/10';

  const headerGradient = isLocked
    ? 'from-amber-500/20 via-slate-900/75 to-slate-950/90'
    : !isValidParity
    ? 'from-red-500/15 to-slate-950/90'
    : diff === 0 && anchorCount > 0
    ? 'from-emerald-500/20 via-emerald-950/25 to-slate-950/90'
    : diff > 0
    ? 'from-amber-500/20 via-amber-950/25 to-slate-950/90'
    : 'from-purple-500/15 via-slate-900/60 to-slate-950/90';

  const fillBarPercent =
    anchorCount > 0
      ? Math.min(100, Math.round((cardCopies.length / anchorCount) * 100))
      : cardCopies.length > 0
      ? 100
      : 0;

  const fillBarColor = !isValidParity
    ? 'bg-red-500'
    : diff === 0 && anchorCount > 0
    ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
    : diff > 0
    ? 'bg-gradient-to-r from-amber-500 to-orange-400'
    : 'bg-gradient-to-r from-purple-500 to-cyan-400';

  // Build Seeable / Draw % Tooltip
  const seeCount = copiesAtMV > 0 ? copiesAtMV : Math.max(anchorCount, 1);
  const seenPlay = cardsSeenByTurn(evalTurn, true) + drawBoost;
  const seenDraw = cardsSeenByTurn(evalTurn, false) + drawBoost;
  const seePctPlay =
    targetDeckSize > 0 && seenPlay > 0
      ? Math.round(probabilityAtLeastOne(targetDeckSize, seeCount, seenPlay) * 100)
      : 0;
  const seePctDraw =
    targetDeckSize > 0 && seenDraw > 0
      ? Math.round(probabilityAtLeastOne(targetDeckSize, seeCount, seenDraw) * 100)
      : 0;
  const seePct = onThePlay ? seePctPlay : seePctDraw;
  const seeColor =
    seePct >= 70
      ? 'text-cyan-300'
      : seePct >= 50
      ? 'text-sky-300'
      : seePct >= 30
      ? 'text-purple-300'
      : 'text-pink-300';
  const seeBorder =
    seePct >= 70
      ? 'border-cyan-500/35 bg-cyan-500/10'
      : seePct >= 50
      ? 'border-sky-500/35 bg-sky-500/10'
      : seePct >= 30
      ? 'border-purple-500/35 bg-purple-500/10'
      : 'border-pink-500/35 bg-pink-500/10';

  const showAdaptive = probTargetTurn !== evalTurn;
  const adaptSeen = (onThePlay ? cardsSeenByTurn(probTargetTurn, true) : cardsSeenByTurn(probTargetTurn, false)) + drawBoost;
  const adaptPct =
    showAdaptive && targetDeckSize > 0 && adaptSeen > 0
      ? Math.round(probabilityAtLeastOne(targetDeckSize, seeCount, adaptSeen) * 100)
      : 0;
  const seenCards = onThePlay ? seenPlay : seenDraw;

  // Build Mana / Castable % Info
  const castBorder =
    castPct >= 70
      ? 'border-emerald-500/35 bg-emerald-500/10'
      : castPct >= 50
      ? 'border-amber-500/35 bg-amber-500/10'
      : castPct >= 30
      ? 'border-orange-500/35 bg-orange-500/10'
      : 'border-rose-500/35 bg-rose-500/10';

  const showAdaptCast = mv >= 1 && probTargetTurn !== evalTurn;
  const adaptCastSeen =
    (onThePlay ? cardsSeenByTurn(probTargetTurn, true) : cardsSeenByTurn(probTargetTurn, false)) + drawBoost;
  const canCastBySliderTurn = probTargetTurn + rampBoost >= mv;
  const adaptCastPct =
    showAdaptCast && canCastBySliderTurn && targetDeckSize > 0 && adjustedLands > 0
      ? Math.round(probabilityLandsByTurn(targetDeckSize, adjustedLands, mv, adaptCastSeen) * 100)
      : 0;

  const lowestOccupiedMV = (() => {
    for (let m = 1; m < mv; m++) {
      const hasCards = displayDeck.some((c) => {
        if (isEffectivelyLand(c) || c.isExtraCard) return false;
        const cmv = c.effectiveMV ?? c.cmc;
        return cmv === m;
      });
      const hasAnchor = m < anchorDistribution.length && anchorDistribution[m] > 0;
      if (hasCards || hasAnchor) return m;
    }
    return 0;
  })();
  const hasLowSlots = lowestOccupiedMV >= 1 && lowestOccupiedMV <= 2;
  const rampAdviceLower = hasLowSlots
    ? `add ramp in MV ${lowestOccupiedMV}-${Math.min(lowestOccupiedMV + 1, mv - 1)} slots`
    : lowestOccupiedMV > 0
    ? `add ramp at MV ${lowestOccupiedMV} or lower your curve`
    : 'lower your curve to include MV 1-2 ramp';

  let verdict = '';
  if (mv === 0) {
    verdict = '✦ Free to cast — 0 mana required';
  } else if (copiesAtMV > 0) {
    if (playPct >= 50) {
      verdict =
        rampBoost > 0
          ? `✦ On curve — +${rampBoost} effective ramp supporting`
          : '✦ Strong on-curve mana consistency';
    } else {
      verdict = castPct < 50 ? `◆ Mana tight — ${rampAdviceLower}` : '◆ Add card draw or copies to see more often';
    }
  } else {
    verdict = castPct >= 70 ? '✦ Mana base ready — fill this slot!' : `◇ Close — ${rampAdviceLower}`;
  }

  // Group duplicate card copies in Base mode (and group identical unsplit copies in Match Plans)
  const groupedItems: GroupedDropCardItem[] = React.useMemo(() => {
    const planKey = activePlan !== 'base' ? (activePlan as 'A' | 'B' | 'C' | 'D' | 'E') : null;
    const map = new Map<string, GroupedDropCardItem>();

    for (const raw of cardCopies) {
      const baseMV = raw.card.effectiveMV ?? raw.card.cmc;
      const displayMV =
        planKey && raw.card.planCopyMVs?.[planKey]?.[raw.copyIndex] !== undefined
          ? raw.card.planCopyMVs[planKey]![raw.copyIndex]!
          : raw.copyMV;

      const overrideCost = planKey ? raw.card.planCopyCosts?.[planKey]?.[raw.copyIndex] : undefined;
      const baseCost = raw.card.manaCost || '';
      const displayCost = overrideCost
        ? overrideCost
            .replace(/([WUBRGC2])\/([WUBRGCP])/gi, '{$1/$2}')
            .replace(/(?<![{/])([0-9]+)(?![}/])/g, '{$1}')
            .replace(/(?<![{/])([WUBRGC])(?![}/])/gi, '{$1}')
        : baseCost;

      const groupKey =
        activePlan === 'base'
          ? raw.card.id
          : `${raw.card.id}__${displayMV}__${overrideCost || ''}__${raw.isMarkedAsLand ? 1 : 0}__${
              raw.isBackFace ? 1 : 0
            }`;

      const existing = map.get(groupKey);
      if (!existing) {
        map.set(groupKey, {
          card: raw.card,
          groupCount: 1,
          copyIndices: [raw.copyIndex],
          displayMV,
          baseMV,
          displayCost,
          isBackFace: raw.isBackFace,
          isMarkedAsLand: raw.isMarkedAsLand,
        });
      } else {
        existing.groupCount += 1;
        existing.copyIndices.push(raw.copyIndex);
      }
    }

    return Array.from(map.values());
  }, [activePlan, cardCopies]);

  const deckLandCount = deck.filter((c) => c.isLand).reduce((sum, c) => sum + c.quantity, 0);
  const deckTotalCards = deck.reduce((sum, c) => sum + c.quantity, 0);
  const hasCommanderInSideboard = sideboard.some((c) => c.isCommander);

  // Column Options ("three lines" ☰) portal menu state & batch handlers
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuPopupRef = useRef<HTMLDivElement>(null);

  const uniqueCardsInColumn = React.useMemo(() => {
    const map = new Map<string, { card: Card; colCopies: number }>();
    for (const raw of cardCopies) {
      const existing = map.get(raw.card.id);
      if (existing) {
        existing.colCopies += 1;
      } else {
        map.set(raw.card.id, { card: raw.card, colCopies: 1 });
      }
    }
    return Array.from(map.values());
  }, [cardCopies]);

  const currentBaseTotal = uniqueCardsInColumn.reduce((sum, { card }) => sum + card.quantity, 0);
  const halfBaseTotal = uniqueCardsInColumn.reduce(
    (sum, { card }) => sum + Math.max(1, Math.floor(card.quantity / 2)),
    0
  );
  const doubleBaseTotal = uniqueCardsInColumn.reduce(
    (sum, { card }) => sum + (card.quantity < 4 ? Math.min(4, card.quantity * 2) : card.quantity * 2),
    0
  );

  const canHalf = !isLocked && uniqueCardsInColumn.some(({ card }) => card.quantity > 1);
  const canDouble = !isLocked && uniqueCardsInColumn.length > 0;
  const canSetSingleton = !isLocked && uniqueCardsInColumn.some(({ card }) => card.quantity > 1);
  const canSetPlayset = !isLocked && uniqueCardsInColumn.some(({ card }) => card.quantity !== 4);
  const canClear = !isLocked && uniqueCardsInColumn.length > 0;

  const overriddenCardsInColumn = React.useMemo(() => {
    if (activePlan === 'base') {
      return uniqueCardsInColumn.filter(
        ({ card }) => card.effectiveMV !== undefined && card.effectiveMV !== card.cmc
      );
    }
    const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
    return uniqueCardsInColumn.filter(({ card }) => {
      const baseMV = card.effectiveMV ?? card.cmc;
      const copyMVs = card.planCopyMVs?.[planKey];
      return Boolean(copyMVs && copyMVs.some((v) => v !== undefined && v !== baseMV));
    });
  }, [activePlan, uniqueCardsInColumn]);

  const canResetMVOverrides = !isLocked && overriddenCardsInColumn.length > 0;

  const openOrToggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    if (menuBtnRef.current) {
      const rect = menuBtnRef.current.getBoundingClientRect();
      const menuWidth = 216;
      const estimatedHeight = 290;
      const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
      const top =
        rect.bottom + estimatedHeight + 8 > window.innerHeight && rect.top - estimatedHeight - 6 > 8
          ? rect.top - estimatedHeight - 6
          : rect.bottom + 6;
      setMenuPos({ top, left });
    }
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (
        menuPopupRef.current &&
        !menuPopupRef.current.contains(target) &&
        menuBtnRef.current &&
        !menuBtnRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setMenuOpen(false);
    };
    const handleScrollOrResize = () => setMenuOpen(false);

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [menuOpen]);

  const handleBatchScaleQuantities = (computeNextQty: (currentQty: number) => number) => {
    if (isLocked || uniqueCardsInColumn.length === 0) return;
    const planKey = activePlan !== 'base' ? (activePlan as 'A' | 'B' | 'C' | 'D' | 'E') : null;
    uniqueCardsInColumn.forEach(({ card }) => {
      const nextQty = Math.max(1, computeNextQty(card.quantity));
      if (nextQty !== card.quantity) {
        onUpdateCard?.(card.id, { quantity: nextQty });
        if (planKey && card.planQuantities?.[planKey] !== undefined) {
          onUpdatePlanQuantity?.(card.id, planKey, nextQty);
        }
      }
    });
    setMenuOpen(false);
  };

  const handleResetColumnMVOverrides = () => {
    if (isLocked || overriddenCardsInColumn.length === 0) return;
    if (activePlan === 'base') {
      overriddenCardsInColumn.forEach(({ card }) => {
        onUpdateCard?.(card.id, { effectiveMV: undefined });
      });
    } else {
      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
      overriddenCardsInColumn.forEach(({ card }) => {
        const baseMV = card.effectiveMV ?? card.cmc;
        onUpdatePlanCopyMVs?.(card.id, planKey, Array(card.quantity).fill(baseMV));
      });
    }
    setMenuOpen(false);
  };

  const handleClearColumn = () => {
    if (isLocked || uniqueCardsInColumn.length === 0) return;
    if (activePlan === 'base') {
      uniqueCardsInColumn.forEach(({ card }) => {
        onRemoveCard?.(card.id);
      });
    } else {
      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
      uniqueCardsInColumn.forEach(({ card, colCopies }) => {
        const currentMain = card.planQuantities?.[planKey] ?? card.quantity;
        onUpdatePlanQuantity?.(card.id, planKey, Math.max(0, currentMain - colCopies));
      });
    }
    setMenuOpen(false);
  };

  return (
    <div
      className={`flex-1 min-w-[168px] snap-start rounded-xl border ${columnBorderClass} bg-slate-950/65 shadow-lg flex flex-col overflow-hidden ${
        !isValidParity ? 'opacity-55' : ''
      }`}
    >
      {/* UPGRADED #DROP COLUMN HEADER */}
      <div className={`px-2 pt-2 pb-1.5 bg-gradient-to-b ${headerGradient} border-b border-white/10`}>
        {/* Top Row: Floor/Peak or Status Pill | Mana Orb + DROPS | Column Options Menu */}
        <div className="flex items-center justify-between gap-1">
          {/* Left Control: Floor / Peak Toggle or Diff Status */}
          <div className="flex items-center flex-shrink-0">
            {mv < Math.floor(targetManaCurve) ? (
              <BrewTooltip
                title={minMVFloor === mv ? 'Clear Curve Floor' : `Set Curve Floor at MV ${mv}`}
                badge={minMVFloor === mv ? 'Active' : `MV ≥ ${mv}`}
                description={`Fold ideal curve targets below MV ${mv} up into MV ${mv} and hide lower columns.`}
                accent="amber"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMinMVFloor(minMVFloor === mv ? null : mv);
                  }}
                  className={`px-1 py-0.5 rounded text-[9px] font-semibold border transition-colors flex items-center gap-0.5 whitespace-nowrap ${
                    minMVFloor === mv
                      ? 'bg-amber-500/30 text-amber-200 border-amber-400/60'
                      : 'bg-white/5 text-white/45 hover:text-white/80 border-white/10'
                  }`}
                >
                  <span>⇥</span>
                  <span>Floor</span>
                </button>
              </BrewTooltip>
            ) : mv > Math.ceil(targetManaCurve) ? (
              <BrewTooltip
                title={maxMVCap === mv ? 'Clear Curve Peak Cap' : `Cap Curve at MV ${mv}`}
                badge={maxMVCap === mv ? 'Active' : `MV ≤ ${mv}`}
                description={`Fold ideal curve targets above MV ${mv} down into MV ${mv} and hide higher columns.`}
                accent="cyan"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMaxMVCap(maxMVCap === mv ? null : mv);
                  }}
                  className={`px-1 py-0.5 rounded text-[9px] font-semibold border transition-colors flex items-center gap-0.5 whitespace-nowrap ${
                    maxMVCap === mv
                      ? 'bg-cyan-500/30 text-cyan-200 border-cyan-400/60'
                      : 'bg-white/5 text-white/45 hover:text-white/80 border-white/10'
                  }`}
                >
                  <span>Peak</span>
                  <span>⇤</span>
                </button>
              </BrewTooltip>
            ) : (
              <span
                className={`text-[9px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${
                  diff === 0 && anchorCount > 0
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                    : diff > 0
                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                    : 'bg-white/5 text-white/45 border-white/10'
                }`}
              >
                {diff === 0 && anchorCount > 0 ? '✓ Fit' : diff > 0 ? `+${diff}` : `${diff}`}
              </span>
            )}
          </div>

          {/* Center: Mana Value Symbol Orb + DROP/DROPS (+ Lock Indicator) */}
          <div
            className={`flex items-center justify-center gap-1 font-extrabold text-xs tracking-wide text-white whitespace-nowrap flex-shrink-0 ${
              !isValidParity ? 'line-through text-red-300/70' : ''
            }`}
          >
            <ManaSymbol symbol={String(mv)} size="sm" />
            <span>{mv === 1 ? 'DROP' : 'DROPS'}</span>
            {isLocked && (
              <BrewTooltip
                title="Column Locked"
                badge={`MV ${mv}`}
                description="Cards in this column are protected from accidental edits. Click to unlock."
                accent="amber"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleLock?.();
                  }}
                  className="ml-0.5 text-amber-400 hover:text-amber-200 transition-colors"
                >
                  <Lock className="size-3" />
                </button>
              </BrewTooltip>
            )}
          </div>

          {/* Right Control: Three-Lines (☰) Column Options Menu */}
          <div className="flex items-center justify-end flex-shrink-0">
            <BrewTooltip
              title={`${mv}-Drop Column Options`}
              badge={isLocked ? 'Locked' : `${cardCopies.length} cards`}
              description="Lock/unlock column, quick-double or quick-halve quantities, set playsets/singletons, or clear all."
              accent={isLocked ? 'amber' : 'purple'}
            >
              <button
                ref={menuBtnRef}
                type="button"
                onClick={openOrToggleMenu}
                aria-expanded={menuOpen}
                aria-label={`${mv}-Drop column options`}
                className={`p-1 rounded-md border transition-colors ${
                  menuOpen
                    ? 'bg-purple-500/35 text-white border-purple-400/60 shadow-sm'
                    : isLocked
                    ? 'bg-amber-500/20 hover:bg-amber-500/35 text-amber-300 border-amber-400/45'
                    : 'bg-white/5 hover:bg-purple-500/25 text-white/60 hover:text-purple-200 border-white/10 hover:border-purple-400/40'
                }`}
              >
                <Menu className="size-3.5" />
              </button>
            </BrewTooltip>

            {menuOpen &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={menuPopupRef}
                  style={{ top: menuPos.top, left: menuPos.left, width: 216 }}
                  onClick={(e) => e.stopPropagation()}
                  className="fixed z-[9999] rounded-xl bg-slate-950/98 backdrop-blur-md border border-purple-500/35 shadow-[0_12px_36px_rgba(0,0,0,0.85)] p-1.5 text-xs select-none space-y-1"
                >
                  {/* Menu Header */}
                  <div className="flex items-center justify-between px-2 py-1 border-b border-white/10">
                    <span className="font-bold text-[11px] text-white flex items-center gap-1.5">
                      <ManaSymbol symbol={String(mv)} size="sm" />
                      <span>{mv === 1 ? '1-Drop' : `${mv}-Drops`}</span>
                    </span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">
                      {cardCopies.length}/{anchorCount} slots
                    </span>
                  </div>

                  {/* Lock / Unlock Toggle */}
                  {onToggleLock && (
                    <button
                      type="button"
                      onClick={() => {
                        onToggleLock();
                        setMenuOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                        isLocked
                          ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/35'
                          : 'hover:bg-white/10 text-white/85'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {isLocked ? (
                          <Unlock className="size-3.5 text-amber-300" />
                        ) : (
                          <Lock className="size-3.5 text-amber-400" />
                        )}
                        <span>{isLocked ? 'Unlock Column' : 'Lock Column'}</span>
                      </span>
                      <span className="text-[9px] font-mono text-white/45">
                        {isLocked ? 'Frozen' : 'Protect'}
                      </span>
                    </button>
                  )}

                  <div className="h-px bg-white/10 my-0.5" />

                  {/* Batch Quantity Scaling: Quick Half (÷2) & Quick Double (×2) */}
                  <button
                    type="button"
                    disabled={!canHalf}
                    onClick={() => handleBatchScaleQuantities((q) => Math.max(1, Math.floor(q / 2)))}
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-medium hover:bg-cyan-500/15 text-white/85 hover:text-cyan-200 disabled:opacity-35 disabled:pointer-events-none transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-4 rounded bg-cyan-500/20 border border-cyan-400/30 text-[9px] font-mono font-bold text-cyan-300">
                        ÷2
                      </span>
                      <span>Quick Half Quantities</span>
                    </span>
                    <span className="text-[9px] font-mono text-white/45">
                      {uniqueCardsInColumn.length === 0
                        ? 'Empty'
                        : currentBaseTotal === halfBaseTotal
                        ? 'Min 1×'
                        : `${currentBaseTotal}→${halfBaseTotal}`}
                    </span>
                  </button>

                  <button
                    type="button"
                    disabled={!canDouble}
                    onClick={() =>
                      handleBatchScaleQuantities((q) => (q < 4 ? Math.min(4, q * 2) : q * 2))
                    }
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-medium hover:bg-purple-500/20 text-white/85 hover:text-purple-200 disabled:opacity-35 disabled:pointer-events-none transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-5 h-4 rounded bg-purple-500/25 border border-purple-400/35 text-[9px] font-mono font-bold text-purple-200">
                        ×2
                      </span>
                      <span>Quick Double Quantities</span>
                    </span>
                    <span className="text-[9px] font-mono text-white/45">
                      {uniqueCardsInColumn.length === 0 ? 'Empty' : `${currentBaseTotal}→${doubleBaseTotal}`}
                    </span>
                  </button>

                  {/* Preset Quantity Pills: All 1× | All 4× */}
                  <div className="grid grid-cols-2 gap-1 px-0.5 pt-0.5">
                    <button
                      type="button"
                      disabled={!canSetSingleton}
                      onClick={() => handleBatchScaleQuantities(() => 1)}
                      className="flex items-center justify-center gap-1 py-1 rounded-md bg-white/5 hover:bg-white/15 border border-white/10 text-[10px] font-semibold text-white/75 hover:text-white disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <span className="font-mono text-cyan-300">1×</span>
                      <span>All Singleton</span>
                    </button>
                    <button
                      type="button"
                      disabled={!canSetPlayset || isSingletonFormat}
                      onClick={() => handleBatchScaleQuantities(() => 4)}
                      className="flex items-center justify-center gap-1 py-1 rounded-md bg-white/5 hover:bg-white/15 border border-white/10 text-[10px] font-semibold text-white/75 hover:text-white disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <span className="font-mono text-amber-300">4×</span>
                      <span>All Playsets</span>
                    </button>
                  </div>

                  <div className="h-px bg-white/10 my-0.5" />

                  {/* Search & Curve Floor/Peak Shortcuts */}
                  {onAddCard && (
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        setBuildSearchMV(mv);
                        setMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-medium hover:bg-white/10 text-white/80 hover:text-white disabled:opacity-35 disabled:pointer-events-none transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Search className="size-3.5 text-purple-300" />
                        <span>Find {mv}-Drop Cards</span>
                      </span>
                      <span className="text-[9px] font-mono text-white/40">MV {mv}</span>
                    </button>
                  )}

                  <div className="grid grid-cols-2 gap-1 px-0.5">
                    {mv > 0 ? (
                      <button
                        type="button"
                        onClick={() => {
                          setMinMVFloor(minMVFloor === mv ? null : mv);
                          setMenuOpen(false);
                        }}
                        className={`flex items-center justify-center gap-1 py-1 rounded-md border text-[10px] font-semibold transition-colors ${
                          minMVFloor === mv
                            ? 'bg-amber-500/25 text-amber-200 border-amber-400/50'
                            : 'bg-white/5 hover:bg-white/15 text-white/70 border-white/10'
                        }`}
                      >
                        <span>⇥</span>
                        <span>{minMVFloor === mv ? 'Unfloor' : 'Set Floor'}</span>
                      </button>
                    ) : (
                      <div />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMaxMVCap(maxMVCap === mv ? null : mv);
                        setMenuOpen(false);
                      }}
                      className={`flex items-center justify-center gap-1 py-1 rounded-md border text-[10px] font-semibold transition-colors ${
                        maxMVCap === mv
                          ? 'bg-cyan-500/25 text-cyan-200 border-cyan-400/50'
                          : 'bg-white/5 hover:bg-white/15 text-white/70 border-white/10'
                      }`}
                    >
                      <span>{maxMVCap === mv ? 'Uncap' : 'Set Peak'}</span>
                      <span>⇤</span>
                    </button>
                  </div>

                  <div className="h-px bg-white/10 my-0.5" />

                  {/* Reset MV Overrides & Clear Column */}
                  <button
                    type="button"
                    disabled={!canResetMVOverrides}
                    onClick={handleResetColumnMVOverrides}
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-medium hover:bg-purple-500/20 text-purple-200/90 hover:text-purple-100 disabled:opacity-35 disabled:pointer-events-none transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <RotateCcw className="size-3.5 text-purple-300" />
                      <span>Reset MV Overrides</span>
                    </span>
                    <span className="text-[9px] font-mono text-purple-300/70">
                      {overriddenCardsInColumn.length > 0 ? `${overriddenCardsInColumn.length} custom` : 'None'}
                    </span>
                  </button>

                  <button
                    type="button"
                    disabled={!canClear}
                    onClick={handleClearColumn}
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] font-semibold hover:bg-red-500/20 text-red-300 hover:text-red-200 disabled:opacity-35 disabled:pointer-events-none transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Trash2 className="size-3.5 text-red-400" />
                      <span>{activePlan === 'base' ? 'Clear All' : 'Clear from Plan'}</span>
                    </span>
                    <span className="text-[9px] font-mono text-red-300/70">
                      {cardCopies.length > 0 ? `${cardCopies.length} cards` : 'Empty'}
                    </span>
                  </button>
                </div>,
                document.body
              )}
          </div>
        </div>

        {/* Middle Row: Slot Fill Counts + Weight + Visual Progress Bar */}
        <div className="mt-1.5 space-y-1">
          <div className="flex items-center justify-between gap-1 text-[10px] whitespace-nowrap">
            <BrewTooltip
              title={`${mv}-Drop Slot Count`}
              badge={`${cardCopies.length} / ${anchorCount} copies`}
              description={`You have ${cardCopies.length} card(s) at MV ${mv} vs. an ideal curve target of ${anchorCount} card(s).`}
              detail={
                diff === 0
                  ? '✦ Column is right on target!'
                  : diff < 0
                  ? `Add ${Math.abs(diff)} more ${mv}-drop(s) to match curve`
                  : `${diff} card(s) above ideal target`
              }
              accent={diff === 0 ? 'green' : diff > 0 ? 'amber' : 'purple'}
            >
              <span className="font-semibold cursor-help flex items-center gap-1">
                <span className="text-white/45 text-[9px]">Slots</span>
                <span
                  className={
                    diff === 0 && anchorCount > 0
                      ? 'text-emerald-300 font-bold'
                      : diff > 0
                      ? 'text-amber-300 font-bold'
                      : 'text-white/90 font-bold'
                  }
                >
                  {cardCopies.length}/{anchorCount}
                </span>
              </span>
            </BrewTooltip>

            <BrewTooltip
              title={`MV ${mv} Column Mana Weight`}
              badge={`${cardCopies.length * mv} / ${anchorCount * mv} weight`}
              description={`Total mana weight contributed by all ${mv}-drops (${cardCopies.length} copies × ${mv} mana = ${
                cardCopies.length * mv
              }).`}
              accent="cyan"
            >
              <span className="text-[9px] text-white/55 font-mono cursor-help">
                Wt <span className="text-white/80 font-semibold">{cardCopies.length * mv}</span>/{anchorCount * mv}
              </span>
            </BrewTooltip>
          </div>

          {/* Visual Slot Progress Bar */}
          <div className="w-full h-1.5 rounded-full bg-black/50 border border-white/5 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${fillBarColor}`}
              style={{ width: `${fillBarPercent}%` }}
            />
          </div>
        </div>

        {/* Bottom Header Row: Clear Labeled "Draw %" & "Mana %" Pills */}
        <div className="mt-1.5 grid grid-cols-2 gap-1">
          <BrewTooltip
            title={`Draw ≥1 ${mv}-Drop by Turn ${evalTurn}`}
            badge={`${seePct}% (${onThePlay ? 'Play' : 'Draw'})`}
            description={`Chance to draw at least 1 of your ${seeCount}× ${
              mv === 0 ? '0-drop' : `${mv}-drop`
            }${seeCount === 1 ? '' : 's'} seeing ${seenCards} cards by Turn ${evalTurn}.`}
            detail={
              showAdaptive
                ? `At Turn ${probTargetTurn}: ${adaptPct}% • P(≥1 of ${seeCount} in ${targetDeckSize})`
                : `P(≥1 of ${seeCount} in ${targetDeckSize} cards)`
            }
            accent="cyan"
            className="w-full"
          >
            <div
              className={`w-full flex items-center justify-between px-1.5 py-0.5 rounded border text-[9px] font-mono cursor-help transition-colors ${seeBorder} ${seeColor} ${
                copiesAtMV === 0 ? 'opacity-60' : ''
              }`}
            >
              <span className="font-sans text-[8px] text-white/60 font-medium">Draw</span>
              <span className="font-bold">{seePct}%</span>
            </div>
          </BrewTooltip>

          <BrewTooltip
            title={mv === 0 ? '0-Drop Mana Cost' : `Hit ${mv} Mana by Turn ${evalTurn}`}
            badge={mv === 0 ? 'Free (100%)' : `${castPct}% (${onThePlay ? 'Play' : 'Draw'})`}
            description={
              mv === 0
                ? 'Costs 0 mana — always castable!'
                : `${castPct}% chance to have ≥${mv} mana sources (${adjustedLands} effective in ${targetDeckSize} cards) by Turn ${evalTurn}.`
            }
            detail={showAdaptCast ? `At Turn ${probTargetTurn}: ${adaptCastPct}% • ${verdict}` : verdict}
            accent={castPct >= 70 ? 'green' : castPct >= 50 ? 'amber' : 'red'}
            className="w-full"
          >
            <div
              className={`w-full flex items-center justify-between px-1.5 py-0.5 rounded border text-[9px] font-mono cursor-help transition-colors ${castBorder} ${castColor}`}
            >
              <span className="font-sans text-[8px] text-white/60 font-medium">Mana</span>
              <span className="font-bold">{mv === 0 ? 'Free' : `${castPct}%`}</span>
            </div>
          </BrewTooltip>
        </div>

        {!isValidParity && cardCopies.length > 0 && (
          <div className="mt-1 text-[9px] text-red-300 bg-red-500/15 border border-red-500/25 rounded px-1.5 py-0.5 text-center">
            ⚠️ Excluded by parity filter
          </div>
        )}
      </div>

      {/* UPGRADED #DROP CARD LIST (Grouped Sleek Art-Banner Bars) */}
      <div className="p-1.5 space-y-1.5 flex-1 flex flex-col">
        {groupedItems.map((group) => {
          const playability =
            probTargetTurn > 0
              ? combinedPlayabilitySeparateTurns(
                  deckTotalCards,
                  group.card.quantity,
                  deckLandCount,
                  probTargetTurn,
                  group.displayMV,
                  onThePlay
                )
              : { combined: 1, cardProb: 1, landProb: 1 };

          const targetCopyIndex = group.copyIndices[group.copyIndices.length - 1] ?? 0;

          return (
            <DropColumnCardTile
              key={`${group.card.id}-${group.displayMV}-${targetCopyIndex}`}
              item={group}
              mv={mv}
              activePlan={activePlan}
              probTargetTurn={probTargetTurn}
              onThePlay={onThePlay}
              playability={playability}
              showSideboardBtn={!hasCommanderInSideboard}
              isLocked={isLocked}
              onCardClick={onCardClick}
              onHoverStart={(c, e) => {
                setHoveredCard(c);
                setMousePos({ x: e.clientX, y: e.clientY });
              }}
              onHoverMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
              onHoverEnd={() => setHoveredCard(null)}
              onDecrement={(e) => {
                e.stopPropagation();
                const newQty = group.card.quantity - 1;
                if (newQty <= 0) {
                  onRemoveCard?.(group.card.id);
                } else {
                  onUpdateCard?.(group.card.id, { quantity: newQty });
                }
              }}
              onIncrement={(e) => {
                e.stopPropagation();
                onUpdateCard?.(group.card.id, { quantity: group.card.quantity + 1 });
              }}
              onMoveToSideboard={(e) => {
                e.stopPropagation();
                if (activePlan === 'base') {
                  if (group.card.quantity > 0) {
                    const sideboardCard = sideboard.find((c) => c.name === group.card.name);
                    if (sideboardCard) {
                      onUpdateSideboardCard?.(sideboardCard.id, { quantity: sideboardCard.quantity + 1 });
                    } else {
                      onAddToSideboard?.({
                        ...group.card,
                        id: `${group.card.id}-side-${Date.now()}`,
                        quantity: 1,
                        isSideboard: true,
                      });
                    }
                    if (group.card.quantity <= 1) {
                      onRemoveCard?.(group.card.id);
                    } else {
                      onUpdateCard?.(group.card.id, { quantity: group.card.quantity - 1 });
                    }
                  }
                } else {
                  const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                  const currentMain = group.card.planQuantities?.[planKey] ?? group.card.quantity;
                  const newMain = Math.max(currentMain - 1, 0);
                  onUpdatePlanQuantity?.(group.card.id, planKey, newMain);
                }
              }}
              onShiftMVLeft={
                activePlan !== 'base'
                  ? (e) => {
                      e.stopPropagation();
                      if (mv > 0) {
                        const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                        const existingOverride = group.card.planCopyMVs?.[planKey]?.[targetCopyIndex];
                        if (existingOverride === undefined && onCardClick && !onUpdatePlanCopyMVs) {
                          onCardClick(group.card);
                        } else if (onUpdatePlanCopyMVs) {
                          const baseMV = group.card.effectiveMV ?? group.card.cmc;
                          const currentMVs =
                            group.card.planCopyMVs?.[planKey] || Array(group.card.quantity).fill(baseMV);
                          const newMVs = [...currentMVs];
                          newMVs[targetCopyIndex] = mv - 1;
                          onUpdatePlanCopyMVs(group.card.id, planKey, newMVs);
                        }
                      }
                    }
                  : undefined
              }
              onShiftMVRight={
                activePlan !== 'base'
                  ? (e) => {
                      e.stopPropagation();
                      const planKey = activePlan as 'A' | 'B' | 'C' | 'D' | 'E';
                      const existingOverride = group.card.planCopyMVs?.[planKey]?.[targetCopyIndex];
                      if (existingOverride === undefined && onCardClick && !onUpdatePlanCopyMVs) {
                        onCardClick(group.card);
                      } else if (onUpdatePlanCopyMVs) {
                        const baseMV = group.card.effectiveMV ?? group.card.cmc;
                        const currentMVs =
                          group.card.planCopyMVs?.[planKey] || Array(group.card.quantity).fill(baseMV);
                        const newMVs = [...currentMVs];
                        newMVs[targetCopyIndex] = mv + 1;
                        onUpdatePlanCopyMVs(group.card.id, planKey, newMVs);
                      }
                    }
                  : undefined
              }
            />
          );
        })}

        {/* Open Curve Slot CTA Button (or Locked Column Badge) */}
        {isLocked ? (
          <button
            type="button"
            onClick={() => onToggleLock?.()}
            className="w-full mt-auto h-[32px] border border-amber-400/25 hover:border-amber-400/50 bg-amber-500/[0.06] hover:bg-amber-500/15 rounded-lg flex items-center justify-center gap-1.5 text-[10px] font-semibold text-amber-300/80 hover:text-amber-200 transition-all"
          >
            <Lock className="size-3 text-amber-400" />
            <span>Column Locked</span>
            <span className="text-[9px] text-white/40">(Unlock)</span>
          </button>
        ) : (
          anchorCount > cardCopies.length &&
          onAddCard && (
            <BrewTooltip
              title={`Add ${mv}-Drop Card`}
              badge={`${anchorCount - cardCopies.length} open slot(s)`}
              description={`Search for cards to fill your remaining ${
                anchorCount - cardCopies.length
              } ideal curve slot(s) at Mana Value ${mv}.`}
              accent="purple"
              className="w-full mt-auto pt-0.5"
            >
              <button
                type="button"
                onClick={() => setBuildSearchMV(mv)}
                className="w-full h-[36px] border border-dashed border-purple-500/25 hover:border-purple-400/60 bg-purple-500/[0.04] hover:bg-purple-500/15 rounded-lg flex items-center justify-center gap-1.5 text-[11px] font-semibold text-white/45 hover:text-purple-200 transition-all"
              >
                <Plus className="size-3 text-purple-400" />
                <span>Add {mv}-Drop</span>
                <span className="text-[10px] text-amber-400/70">({anchorCount - cardCopies.length} left)</span>
              </button>
            </BrewTooltip>
          )
        )}

        {/* Overflow Badge */}
        {!isLocked && cardCopies.length > anchorCount && (
          <div className="mt-auto pt-0.5 text-[9px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-md text-center py-0.5 font-medium">
            +{cardCopies.length - anchorCount} over curve target
          </div>
        )}
      </div>
    </div>
  );
}
